import { z } from 'zod'
import { websiteDomainSchema } from '@dashboard/formValidationschemas'

export const trialActivationRequestSchema = z.object({
  activation_id: z.string(),
  price_id: z.string(),
  expected_trial_end: z.number().int(),
  charge_now: z.boolean(),
  setup_intent_id: z.string().optional(),
  enable_paid_renewal: z.literal(true)
})

export type TrialActivationRequest = z.infer<typeof trialActivationRequestSchema>

export const trialActivationSchema = z.object({
  subscription_id: z.string(),
  trial_ends_at: z.string(),
  trial_end: z.number().int(),
  charge_now: z.boolean(),
  pending_activation: trialActivationRequestSchema.optional()
})

export type TrialActivation = z.infer<typeof trialActivationSchema>

export const planTierSchema = z.enum(['grow', 'scale', 'pro'])
export const billingIntervalSchema = z.enum(['monthly', 'yearly'])

/**
 * What the project will be charged when its trial ends. Present only while a
 * trial is running with paid renewal enabled: a card trial, or a no-card trial
 * whose owner confirmed a plan. Null otherwise.
 */
export const renewalPlanSchema = z.object({
  tier: planTierSchema,
  interval: billingIntervalSchema,
  amount: z.string(),
  amount_cents: z.number().int(),
  starts_at: z.string(),
  trial_kind: z.enum(['no_card', 'card'])
})

export type RenewalPlan = z.infer<typeof renewalPlanSchema>

/**
 * What starting this project's own subscription does (ENG-1600): a trial with no charge
 * today, or each price's charge today after any agency discount. Only an organization's
 * first billed project trials, so the server decides; never assume a trial.
 */
export const projectStartSchema = z.object({
  trial_days: z.number().int().nullable(),
  charge_today: z.record(z.string(), z.string()),
  agency_discount_percent: z.number().nullable()
})

export type ProjectStart = z.infer<typeof projectStartSchema>

/** One thing the current setup uses that needs a plan above Grow. */
export const setupRequirementItemSchema = z.object({
  key: z.string(),
  tier: z.enum(['scale', 'pro']),
  label: z.string()
})

export const setupRequirementsSchema = z.object({
  required_tier: planTierSchema,
  items: z.array(setupRequirementItemSchema)
})

export type SetupRequirements = z.infer<typeof setupRequirementsSchema>

export const planChoiceSchema = z.object({
  tier: planTierSchema,
  interval: billingIntervalSchema
})

export type PlanChoice = z.infer<typeof planChoiceSchema>

const userSchema = z.object({
  id: z.number().int(),
  name: z.string().nullable(),
  email: z.string().email(),
  email_verified_at: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  password: z.string(),
  free_account: z.boolean(),
  trial_ends_at: z.date().nullable(),
  is_admin: z.boolean().default(false),
  on_trial: z.boolean().optional(),
  current_billing_plan: z.string().nullable().optional(),
  stripe_status: z.string().nullable().optional(),
  stripe_id: z.string().nullable().optional(),
  has_payment_method: z.boolean().optional(),
  trial_activation: trialActivationSchema.nullable().optional(),
  project_trial_days: z.number().int().nullable().optional(),
  renewal_plan: renewalPlanSchema.nullable().optional(),
  billing_model: z.enum(['legacy', 'per_team']).optional(),
  pricing_version: z.string().nullable().optional(),
  is_free_builder: z.boolean().optional(),
  requires_reactivation: z.boolean().optional(),
  requires_payment_recovery: z.union([z.literal(false), z.object({ plan_name: z.string() })]).optional(),
  permissions: z.record(z.string(), z.boolean()).optional(),
  plan: z
    .object({
      tier: z.enum(['grow', 'scale', 'pro', 'legacy', 'legacy_cta']),
      features: z.array(z.string()).optional(),
      esp_providers: z.array(z.string()).optional(),
      limits: z
        .object({
          projects: z.object({ current: z.number(), max: z.number() }),
          collaborators: z.object({ current: z.number(), max: z.number() })
        })
        .optional()
    })
    .optional(),
  active_subscription: z
    .object({
      stripe_price: z.string().optional(),
      stripe_status: z.string().nullable().optional(),
      ends_at: z.string().nullable().optional(),
      created_at: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  first_promoter_ref_id: z.string().nullable(),
  owned_teams: z.array(
    z.object({
      id: z.number().int().optional(),
      pid: z.union([z.string(), z.number()]),
      name: z.string().optional(),
      website: z.string().nullable().optional()
    })
  ),
  /** Friday weekly performance report. */
  weekly_emails_enabled: z.boolean().default(true),
  /** Monday optimization ideas email. */
  optimization_emails_enabled: z.boolean().default(true),
  /** Saved theme; null until the user picks one, in which case the browser's stored choice applies. */
  theme: z.enum(['light', 'dark', 'system']).nullable().optional(),
  /** Refresh the editor when a teammate or an AI assistant changes the project. */
  live_updates_enabled: z.boolean().default(true),
  two_factor_enabled: z.boolean().default(false),
  /** Two-factor setup started but the first code was never confirmed. */
  two_factor_pending: z.boolean().default(false),
  effective_permissions: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
  // Agency fields
  is_agency: z.boolean().default(false),
  managed_by_agency: z.boolean().default(false),
  agency_owner_id: z.number().int().nullable().optional(),
  // Agency info (when user is an agency owner)
  agency: z
    .object({
      name: z.string().nullable(),
      logo: z.string().nullable(),
      slug: z.string().nullable(),
      tagline: z.string().nullable(),
      website: z.string().nullable()
    })
    .nullable()
    .optional(),
  /** Organizations the user belongs to (`GET /api/organizations` rows). */
  organizations: z
    .array(
      z.object({
        pid: z.string(),
        name: z.string(),
        kind: z.enum(['customer', 'agency']),
        role: z.enum(['owner', 'admin', 'member']),
        project_access: z.enum(['all', 'selected']),
        demo_projects: z.object({ used: z.number(), limit: z.number() }).nullable()
      })
    )
    .optional()
})

export type User = z.infer<typeof userSchema>

const registerUserSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  website: websiteDomainSchema
})

export type RegisterUser = z.infer<typeof registerUserSchema>

/** `PUT /user/profile-information` accepts only these; other user fields have their own endpoints. */
const updateUserSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name.'),
  email: z.string().trim().email('Enter a valid email address.')
})

export type UpdateUser = z.infer<typeof updateUserSchema>

/** The form asks for the new password once; `password_confirmation` repeats it for Fortify. */
const updatePasswordSchema = z.object({
  current_password: z.string().min(1, 'Enter your current password.'),
  password: z.string().min(8, 'Use at least 8 characters.')
})

export type UpdatePassword = z.infer<typeof updatePasswordSchema>

export { userSchema, registerUserSchema, updateUserSchema, updatePasswordSchema }
